import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { guidedTourTypes, timeSlots } from './data';
import { CheckIcon } from '../../assets/icons';
import Text from '../text';
import { getBookedTimeSlots } from '../../service';
import Spinner from '../spinner';
import { ImInfo } from 'react-icons/im';

const TimeSlotsForm = ({
  visitDay, // thu | fri
  tourType = 'introduction',
  timeSlot,
  changeTimeSlot,
  guidedTourType,
  changeGuidedTourType,
  professionId,
  changeProfessionId,
  translationPrefix,
  error,
  classDetailRef,
  disabled,
}) => {
  const { t } = useTranslation();

  const [timeSlotOptions, setTimeSlotOptions] = useState([]);
  const [guidedTourProfessions, setGuidedTourProfessions] = useState([]);
  const [showInfo, setShowInfo] = useState(false);
  const [loading, setLoading] = useState(false);

  const infoPopupRef = useRef(null);

  const fetchBookedTimeSlots = async () => {
    try {
      setLoading(true);
      const { data } = await getBookedTimeSlots();
      const updatedTimeSlots = timeSlots[tourType].map((slot) => {
        const dayData = data[visitDay]?.[tourType] || {};

        // Check if slot is disabled for the current visit day
        const isDisabledForDay = slot?.disabledDays?.includes(visitDay);

        if (!slot?.professionSlots) {
          const slotData = dayData[slot.value];
          return {
            ...slot,
            available: isDisabledForDay
              ? false
              : slotData?.available !== undefined
                ? slotData.available > 0
                : true,
          };
        } else {
          const slotData = dayData[slot.value];
          const updatedProfessionSlots = [...slot?.professionSlots]?.map((pS) => {
            const targetSlot = slotData?.[pS?.professionId];
            if (targetSlot) {
              return {
                ...pS,
                available: isDisabledForDay
                  ? false
                  : targetSlot?.available !== undefined
                    ? targetSlot.available > 0
                    : true,
              };
            }
            return { ...pS, available: isDisabledForDay ? false : true };
          });

          return {
            ...slot,
            professionSlots: updatedProfessionSlots,
            available: isDisabledForDay
              ? false
              : updatedProfessionSlots?.some((pS) => pS?.available),
          };
        }
      });
      setTimeSlotOptions(updatedTimeSlots);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookedTimeSlots();
  }, [tourType, visitDay]);

  useEffect(() => {
    if (!guidedTourType) return;

    const guidedTourTypeObj = guidedTourTypes?.find((gType) => gType?.value === guidedTourType);

    if (!guidedTourTypeObj) return;

    const guidedOptionsByTime = timeSlots?.guided?.find(
      (guidedTour) => guidedTour?.value === timeSlot,
    )?.professionSlots;

    if (!guidedOptionsByTime) return;

    const parsedList = guidedTourTypeObj?.professions?.map((gTypeProfession) => {
      if (guidedOptionsByTime?.find((gobt) => gobt?.professionId === gTypeProfession?.value))
        return {
          ...gTypeProfession,
          color: guidedTourTypeObj?.color,
          color2: guidedTourTypeObj?.color2,
        };
    });

    setGuidedTourProfessions([...parsedList?.filter((item) => item)]);
  }, [guidedTourType]);

  useEffect(() => {
    if (!infoPopupRef.current || !classDetailRef.current) return;

    const { bottom, height } = infoPopupRef.current.getBoundingClientRect();
    const { bottom: classBottom } = classDetailRef.current.getBoundingClientRect();

    if (bottom > classBottom) {
      infoPopupRef.current.style.top = `-${height + 10}px`;
    } else {
      infoPopupRef.current.style.top = undefined;
    }
  }, [infoPopupRef.current, classDetailRef.current]);

  return (
    <div className="w-full">
      <Text
        className="mt-12 w-full text-base text-gray-10 text-center"
        type={'medium'}
        text={t(`${translationPrefix}.classDetailsForm.${tourType}.heading`)}
      />
      <Text
        className="mb-6 w-full text-gray-10 text-center"
        type={'xsmall'}
        text={t(`${translationPrefix}.classDetailsForm.${tourType}.subHeading`)}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 xl:w-4/5 mx-auto gap-4 justify-center mt-6 relative">
        {timeSlotOptions?.map((slot) => (
          <div
            key={slot.value}
            className={`relative ${loading ? 'opacity-50 animate-pulse' : ''} ${
              !slot.available || disabled ? 'opacity-50' : ''
            }`}>
            <label
              htmlFor={`timeslot-${slot.value}`}
              className={`flex items-center justify-between gap-4 bg-lightBlue-20 rounded-xl px-4 py-3 ${!slot.available || disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
              <span className="text-sm text-blue-10 font-semibold">{slot.label}</span>
              <input
                type="radio"
                id={`timeslot-${slot.value}`}
                name="timeSlot"
                value={slot.value}
                checked={timeSlot === slot.value}
                onChange={() => slot.available && changeTimeSlot(slot.value)}
                className="absolute top-0 left-0 opacity-0 w-0 h-0"
                disabled={disabled}
              />
              <span
                className={`bg-${timeSlot === slot.value ? 'red-10' : 'white'} rounded-lg w-6 h-6 p-1 flex items-center justify-center`}>
                {timeSlot === slot.value && <CheckIcon />}
              </span>
            </label>
          </div>
        ))}

        {loading && (
          <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center text-blue-10">
            <Spinner size={50} />
          </div>
        )}
      </div>

      {!loading && tourType === 'guided' && timeSlot && (
        <>
          <span className="block my-10 bg-black h-[0.5px] w-full" />
          <Text
            className="w-full text-base text-gray-10 text-center"
            type={'medium'}
            text={t(`${translationPrefix}.classDetailsForm.${tourType}.guidedTourTypeHeading`)}
          />
          <div className="flex flex-wrap justify-center gap-4 xl:w-4/5 mx-auto mt-6">
            {guidedTourTypes.map((gType) => {
              const show = timeSlots?.guided
                ?.find((guidedTour) => guidedTour?.value === timeSlot)
                ?.professionSlots?.some((pS) => pS?.guidedTourType === gType?.value);

              if (!show) return null;

              const available = timeSlotOptions
                ?.find((slot) => slot?.value === timeSlot)
                ?.professionSlots?.some(
                  (pS) => pS?.guidedTourType === gType?.value && pS?.available,
                );

              return (
                <div
                  key={gType.value}
                  className={`relative ${loading ? 'opacity-50 animate-pulse pointer-events-none' : ''} ${
                    !available || disabled ? 'opacity-50' : ''
                  }`}>
                  <label
                    htmlFor={`guidedTourType-${gType.value}`}
                    className={`flex items-center justify-between gap-4 bg-lightBlue-20 rounded-xl px-4 py-3 h-full ${!available || disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    style={{
                      backgroundColor: gType?.color,
                    }}>
                    <span className="text-sm text-white font-semibold">{gType.label}</span>
                    <input
                      type="radio"
                      id={`guidedTourType-${gType.value}`}
                      name="guidedTourType"
                      value={gType.value}
                      checked={guidedTourType === gType.value}
                      onChange={() => available && changeGuidedTourType(gType.value)}
                      className="absolute top-0 left-0 opacity-0 w-0 h-0"
                      disabled={disabled}
                    />
                    <span
                      className={`bg-${guidedTourType === gType.value ? 'red-10' : 'white'} rounded-lg w-6 h-6 p-1 flex items-center justify-center`}>
                      {guidedTourType === gType.value && <CheckIcon />}
                    </span>
                  </label>
                </div>
              );
            })}
          </div>
        </>
      )}

      {!loading && tourType === 'guided' && guidedTourType && guidedTourProfessions?.length > 0 && (
        <>
          <span className="block my-10 bg-black h-[0.5px] w-full" />
          <Text
            className="w-full text-base text-gray-10 text-center"
            type={'medium'}
            text={t(`${translationPrefix}.classDetailsForm.${tourType}.${guidedTourType}.heading`)}
          />
          <div className="flex flex-wrap justify-center gap-4 xl:w-4/5 mx-auto mt-6">
            {guidedTourProfessions.map((gTypeProfession) => {
              const available = timeSlotOptions
                ?.find((slot) => slot?.value === timeSlot)
                ?.professionSlots?.some(
                  (pS) => pS?.professionId === gTypeProfession?.value && pS?.available,
                );

              return (
                <div
                  key={gTypeProfession.value}
                  className={`relative ${loading ? 'opacity-50 animate-pulse pointer-events-none' : ''} ${
                    !available || disabled ? 'opacity-50' : ''
                  }`}>
                  <label
                    htmlFor={`guidedTourType-${gTypeProfession.value}`}
                    className={`relative flex items-center justify-between gap-4 bg-lightBlue-20 rounded-xl px-4 py-3 h-full ${!available || disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    style={{
                      backgroundColor: gTypeProfession?.color2,

                      zIndex: showInfo === gTypeProfession?.value ? 10 : undefined,
                    }}>
                    {guidedTourType === 'type1' && (
                      <span
                        className="relative"
                        onClick={(e) => {
                          e.preventDefault();

                          setShowInfo(
                            showInfo === gTypeProfession?.value ? null : gTypeProfession?.value,
                          );
                        }}>
                        <ImInfo color="#C54ECD" />

                        {showInfo === gTypeProfession?.value && (
                          <span
                            ref={infoPopupRef}
                            className="absolute top-[140%] left-0 bg-white rounded-lg p-2 w-64 shadow-lg"
                            dangerouslySetInnerHTML={{
                              __html: t(
                                `${translationPrefix}.classDetailsForm.${tourType}.${guidedTourType}.${gTypeProfession?.value}.info`,
                              ),
                            }}
                          />
                        )}
                      </span>
                    )}
                    <span
                      className="text-sm text-white font-semibold"
                      style={{
                        color: gTypeProfession?.color,
                      }}>
                      {gTypeProfession.label}
                    </span>
                    <input
                      type="radio"
                      id={`guidedTourType-${gTypeProfession.value}`}
                      name="guidedTourType"
                      value={gTypeProfession.value}
                      checked={professionId === gTypeProfession.value}
                      onChange={() => available && changeProfessionId(gTypeProfession.value)}
                      className="absolute top-0 left-0 opacity-0 w-0 h-0"
                      disabled={disabled}
                    />
                    <span
                      className={`bg-${professionId === gTypeProfession.value ? 'red-10' : 'white'} rounded-lg w-6 h-6 p-1 flex items-center justify-center`}>
                      {professionId === gTypeProfession.value && <CheckIcon />}
                    </span>
                  </label>
                </div>
              );
            })}
          </div>
        </>
      )}
      {error && (
        <p
          className="error-field text-red-20 text-xl font-semibold mt-6 text-center animate-bounce"
          dangerouslySetInnerHTML={{ __html: t(`errors.${error}`) }}
        />
      )}
    </div>
  );
};

export default TimeSlotsForm;
